import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, TextField, Button, Alert, Switch, FormControlLabel,
  Card, CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categoryDialog, setCategoryDialog] = useState({ open: false, mode: 'add', data: null });

  // 統計數據
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/categories');
      if (response.data.success) {
        const cats = response.data.categories || [];
        setCategories(cats);
        calculateStats(cats);
      }
    } catch (error) {
      toast.error('載入分類失敗');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (cats) => {
    const total = cats.length;
    const active = cats.filter(c => c.is_active).length;
    const inactive = total - active;
    setStats({ total, active, inactive });
  };

  const handleCategorySubmit = async () => {
    const { mode, data } = categoryDialog;
    if (!data?.name?.trim()) {
      toast.error('請填寫分類名稱');
      return;
    }

    setSaving(true);
    try {
      if (mode === 'add') {
        await api.post('/settings/categories', data);
        toast.success('分類已新增');
      } else {
        await api.put(`/settings/categories/${data.id}`, data);
        toast.success('分類已更新');
      }
      setCategoryDialog({ open: false, mode: 'add', data: null });
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.error || '操作失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryDelete = async (id) => {
    if (!window.confirm('確定要刪除此分類嗎？刪除後將無法復原。')) return;
    try {
      await api.delete(`/settings/categories/${id}`);
      toast.success('分類已刪除');
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.error || '刪除失敗');
    }
  };

  const moveCategoryOrder = async (id, direction) => {
    const index = categories.findIndex(c => c.id === id);
    if ((direction === -1 && index === 0) || (direction === 1 && index === categories.length - 1)) return;

    const newCategories = [...categories];
    const temp = newCategories[index];
    newCategories[index] = newCategories[index + direction];
    newCategories[index + direction] = temp;

    const items = newCategories.map((c, i) => ({ id: c.id, sort_order: i + 1 }));
    try {
      await api.put('/settings/categories/batch/sort', { items });
      setCategories(newCategories.map((c, i) => ({ ...c, sort_order: i + 1 })));
      toast.success('排序已更新');
    } catch (error) {
      toast.error('更新排序失敗');
    }
  };

  const toggleCategoryStatus = async (category) => {
    try {
      await api.put(`/settings/categories/${category.id}`, {
        ...category,
        is_active: category.is_active ? 0 : 1
      });
      loadCategories();
      toast.success(`分類已${category.is_active ? '停用' : '啟用'}`);
    } catch (error) {
      toast.error('更新狀態失敗');
    }
  };

  const openAddDialog = () => {
    setCategoryDialog({
      open: true,
      mode: 'add',
      data: {
        name: '',
        description: '',
        is_active: 1,
        sort_order: categories.length + 1
      }
    });
  };

  const openEditDialog = (category) => {
    setCategoryDialog({
      open: true,
      mode: 'edit',
      data: { ...category }
    });
  };

  return (
    <Box>
      {/* 頂部統計 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">全部分類</Typography>
              <Typography variant="h4" color="primary">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">已啟用</Typography>
              <Typography variant="h4" color="success.main">{stats.active}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">已停用</Typography>
              <Typography variant="h4" color="text.disabled">{stats.inactive}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 標題與操作 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          分類管理
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
          >
            新增分類
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadCategories}
            disabled={loading}
          >
            重新整理
          </Button>
        </Box>
      </Box>

      {/* 說明 */}
      <Alert severity="info" sx={{ mb: 2 }}>
        管理商品分類，可調整排序、啟用或停用分類。啟用的分類會顯示在前台商品篩選和導航列中。
      </Alert>

      {/* 分類列表 */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell width={100}>排序</TableCell>
              <TableCell>分類名稱</TableCell>
              <TableCell>描述</TableCell>
              <TableCell width={100} align="center">狀態</TableCell>
              <TableCell width={150} align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((cat, index) => (
              <TableRow key={cat.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => moveCategoryOrder(cat.id, -1)}
                      disabled={index === 0}
                    >
                      <UpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveCategoryOrder(cat.id, 1)}
                      disabled={index === categories.length - 1}
                    >
                      <DownIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold">{cat.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {cat.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {cat.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={cat.is_active ? '啟用' : '停用'}
                    color={cat.is_active ? 'success' : 'default'}
                    size="small"
                    onClick={() => toggleCategoryStatus(cat)}
                    sx={{ cursor: 'pointer' }}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => openEditDialog(cat)} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleCategoryDelete(cat.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {categories.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {loading ? '載入中...' : '尚無分類資料，請新增分類'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 分類編輯對話框 */}
      <Dialog
        open={categoryDialog.open}
        onClose={() => setCategoryDialog({ open: false, mode: 'add', data: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {categoryDialog.mode === 'add' ? '新增分類' : '編輯分類'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="分類名稱"
                value={categoryDialog.data?.name || ''}
                onChange={(e) => setCategoryDialog(prev => ({
                  ...prev,
                  data: { ...prev.data, name: e.target.value }
                }))}
                required
                placeholder="例如：生活用品"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="分類描述"
                multiline
                rows={3}
                value={categoryDialog.data?.description || ''}
                onChange={(e) => setCategoryDialog(prev => ({
                  ...prev,
                  data: { ...prev.data, description: e.target.value }
                }))}
                placeholder="簡述此分類的商品類型"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!categoryDialog.data?.is_active}
                    onChange={(e) => setCategoryDialog(prev => ({
                      ...prev,
                      data: { ...prev.data, is_active: e.target.checked ? 1 : 0 }
                    }))}
                  />
                }
                label="啟用分類"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                停用的分類不會顯示在前台
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialog({ open: false, mode: 'add', data: null })}>
            取消
          </Button>
          <Button variant="contained" onClick={handleCategorySubmit} disabled={saving}>
            {categoryDialog.mode === 'add' ? '新增' : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCategories;
